import {
  Camera,
  BellRing,
  ChevronRight,
  Crown,
  Heart,
  LogOut,
  Mail,
  Save,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, json } from "../api";
import { useLocale } from "../App";
import { BottomNav, PhoneShell } from "../components/Shell";
import { compressImage } from "../media";
import {
  enableNativePush,
  clearNativeMessageNotification,
  getNativePushState,
  isNativeApp,
  type NativePushState,
} from "../native";

type Me = {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
  interests: string[];
  account_status: "temporary" | "formal";
  is_vip: boolean;
  vip_expires_at: string | null;
};
export function AccountPage() {
  const lang = useLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/me") });
  const [name, setName] = useState("");
  const [interests, setInterests] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [pushState, setPushState] = useState<NativePushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const avatarInput = useRef<HTMLInputElement>(null);
  const hydratedUserId = useRef<string | null>(null);
  useEffect(() => {
    if (me.data && hydratedUserId.current !== me.data.id) {
      hydratedUserId.current = me.data.id;
      setName(me.data.name);
      setInterests(me.data.interests.join(", "));
      setEmail(me.data.email ?? "");
    }
  }, [me.data]);
  useEffect(() => {
    if (!me.data || !isNativeApp()) return;
    void getNativePushState()
      .then(setPushState)
      .catch((error) =>
        setPushState({
          native: true,
          permission: "prompt",
          registered: false,
          serverConfigured: false,
          error: (error as Error).message,
        }),
      );
  }, [me.data]);
  const save = useMutation({
    mutationFn: () =>
      api(
        "/me",
        json("PATCH", {
          name,
          interests: interests
            .split(/[,，]/)
            .map((item) => item.trim())
            .filter(Boolean),
        }),
      ),
    onSuccess: async () => {
      setNotice(t("accountSaved"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["me"] }),
        queryClient.invalidateQueries({ queryKey: ["session"] }),
      ]);
    },
  });
  const register = useMutation({
    mutationFn: () => api("/auth/register", json("POST", { email, password })),
    onSuccess: async () => {
      setPassword("");
      setNotice(t("registered"));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["me"] }),
        queryClient.invalidateQueries({ queryKey: ["session"] }),
      ]);
    },
  });
  const logout = useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSuccess: async () => {
      await clearNativeMessageNotification();
      queryClient.clear();
      navigate(`/${lang}/start`);
    },
  });
  const uploadAvatar = async (file: File) => {
    const compressed = await compressImage(file, 1024, 0.8);
    const body = new FormData();
    body.append("file", compressed);
    const queued = await api<{ upload: { id: string } }>("/uploads/avatar", {
      method: "POST",
      body,
    });
    for (let attempt = 0; attempt < 30; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
      const status = await api<{
        upload: { status: string; failure_reason?: string };
      }>(`/uploads/${queued.upload.id}`);
      if (status.upload.status === "ready") {
        setNotice(t("accountSaved"));
        await queryClient.invalidateQueries({ queryKey: ["me"] });
        return;
      }
      if (status.upload.status === "failed")
        throw new Error(status.upload.failure_reason ?? "Upload failed");
    }
    throw new Error("Upload processing timed out");
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <PhoneShell className="account-page">
      <div className="account-hero">
        <div className="account-top">
          <h1>{t("profile")}</h1>
        </div>
        <div className="account-identity">
          <div className="avatar-editor">
            {me.data?.avatar_url ? (
              <img src={me.data.avatar_url} alt="" />
            ) : (
              <UserRound />
            )}
            <input
              ref={avatarInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file)
                  void uploadAvatar(file).catch((error) =>
                    setNotice((error as Error).message),
                  );
              }}
            />
            <button
              aria-label="Upload avatar"
              onClick={() => avatarInput.current?.click()}
            >
              <Camera />
            </button>
          </div>
          <div>
            <h2>{me.data?.name}</h2>
            <p>
              {me.data?.account_status === "formal" ? (
                <>
                  <ShieldCheck />
                  {t("formalAccount")}
                </>
              ) : (
                t("temporaryAccount")
              )}
            </p>
          </div>
        </div>
        <div className={`vip-card ${me.data?.is_vip ? "active" : ""}`}>
          <span>
            <Crown />
          </span>
          <div>
            <b>{me.data?.is_vip ? "VIP Active" : "VIP"}</b>
            <small>
              {me.data?.is_vip
                ? t("vipActiveCopy")
                : t("vipInactiveCopy")}
            </small>
          </div>
          <ChevronRight />
        </div>
      </div>
      <div className="account-scroll">
        <form className="profile-form" onSubmit={submit}>
          <label>
            {t("name")}
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={40}
            />
          </label>
          <label>
            {t("interests")}
            <div className="input-with-icon">
              <Heart />
              <input
                value={interests}
                onChange={(event) => setInterests(event.target.value)}
                placeholder={
                  lang === "zh" ? "旅行, 咖啡, 电影" : lang === "vn" ? "Du lịch, Cà phê, Phim ảnh" : "Travel, Coffee, Movies"
                }
              />
            </div>
          </label>
          <button className="primary">
            <Save />
            {t("save")}
          </button>
        </form>
        {isNativeApp() && (
          <section
            className={`notification-card ${pushState?.registered ? "active" : ""}`}
          >
            <span>
              <BellRing />
            </span>
            <div>
              <h3>{t("messageNotifications")}</h3>
              <p>
                {pushState?.permission === "denied"
                  ? t("notificationsDenied")
                  : pushState?.registered && pushState.serverConfigured
                    ? t("notificationsActive")
                    : pushState?.registered
                      ? t("notificationsServerPending")
                      : t("notificationsDescription")}
              </p>
              {pushState?.error && <small>{pushState.error}</small>}
            </div>
            <button
              type="button"
              disabled={pushBusy}
              onClick={() => {
                setPushBusy(true);
                void enableNativePush(lang)
                  .then(setPushState)
                  .catch((error) =>
                    setPushState({
                      native: true,
                      permission: "prompt",
                      registered: false,
                      serverConfigured: false,
                      error: (error as Error).message,
                    }),
                  )
                  .finally(() => setPushBusy(false));
              }}
            >
              {pushBusy
                ? t("enablingNotifications")
                : pushState?.registered
                  ? t("refreshNotifications")
                  : t("enableNotifications")}
            </button>
          </section>
        )}
        {me.data?.account_status === "temporary" && (
          <section className="register-card">
            <div>
              <Mail />
              <span>
                <h3>{t("register")}</h3>
                <p>
                  {t("accountRegisterCopy")}
                </p>
              </span>
            </div>
            <label>
              {t("email")}
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              {t("password")}
              <input
                type="password"
                value={password}
                minLength={8}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button
              className="primary"
              disabled={!email || password.length < 8 || register.isPending}
              onClick={() => register.mutate()}
            >
              {t("register")}
            </button>
          </section>
        )}
        {notice && <p className="form-notice">{notice}</p>}
        <button className="logout-button" onClick={() => logout.mutate()}>
          <LogOut />
          {t("logout")}
        </button>
        <p className="geoip-attribution">
          This product includes GeoLite2 Data created by MaxMind, available from{" "}
          <a href="https://www.maxmind.com" target="_blank" rel="noreferrer">MaxMind</a>.
        </p>
      </div>
      <BottomNav />
    </PhoneShell>
  );
}
